import { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Paper,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import { useAtomValue } from "jotai";
import { DataChatMessage, postDataChatFeedback } from "../api/dataChat";
import { AppAtoms } from "../lib/store";
import { CONST } from "../config/const";
import ChartRenderer from "./chart/ChartRenderer";
import { ChartData, ChartType } from "./chart/types";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function parseChartData(wsData: Record<string, unknown>): ChartData {
  const chartType = (wsData.chart_type as ChartType) ?? "table";
  const chartConfig = (wsData.chart_config ?? {}) as Record<string, unknown>;
  const rawData = wsData.data as Record<string, unknown>[] | null;

  let rows: Record<string, unknown>[];
  if (rawData && rawData.length > 0) {
    rows = rawData;
  } else if (chartType === "kpi" && chartConfig.value != null) {
    rows = [{ value: chartConfig.value }];
  } else {
    rows = [];
  }

  const columns =
    rows.length > 0
      ? Object.keys(rows[0])
      : (chartConfig.columns as { key: string }[] | undefined)?.map(
          (c) => c.key
        ) ?? [];

  const xKey = chartConfig.x_key as string | undefined;
  const yKey = chartConfig.y_key as string | undefined;
  const yKeys = yKey ? [yKey] : undefined;
  const title = chartConfig.title as string | undefined;

  return { chartType, rows, columns, title, xKey, yKeys };
}

function DataChatPanel() {
  const [messages, setMessages] = useState<DataChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [chatLogId, setChatLogId] = useState<number | null>(null);
  const [feedbackSent, setFeedbackSent] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const wsRef = useRef<WebSocket | null>(null);
  const streamingAnswerRef = useRef<string>("");

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  const handleSend = useCallback(
    (overrideMsg?: string) => {
      const msg = overrideMsg ?? inputValue;
      if (!msg.trim()) return;
      setSuggestedQuestions([]);

      const userMsg: DataChatMessage = { role: "user", content: msg };
      setMessages([userMsg]);
      setInputValue("");
      setLoading(true);
      streamingAnswerRef.current = "";

      const idToken = loginUser?.idToken;
      if (!idToken) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "認証情報が取得できません。再ログインしてください。",
          },
        ]);
        setLoading(false);
        return;
      }

      const url = CONST.aiChatAnalysisWebSocketAPIURL + "?Authorization=" + idToken;
      const websocket = new WebSocket(url);
      wsRef.current = websocket;

      websocket.onopen = () => {
        websocket.send(
          JSON.stringify({
            question: msg,
            user_authority: loginUser?.authority || "",
          })
        );
      };

      websocket.onmessage = (event) => {
        const data = JSON.parse(event.data) as Record<string, unknown>;

        if (data.type === "chunk") {
          streamingAnswerRef.current += (data.content as string) ?? "";
          setMessages((prev) => {
            const updated = [...prev];
            if (
              updated.length > 0 &&
              updated[updated.length - 1].role === "assistant"
            ) {
              updated[updated.length - 1] = {
                ...updated[updated.length - 1],
                content: streamingAnswerRef.current,
              };
            } else {
              updated.push({
                role: "assistant",
                content: streamingAnswerRef.current,
              });
            }
            return updated;
          });
        } else if (data.type === "data") {
          const chartData = parseChartData(data);
          setMessages((prev) => {
            const updated = [...prev];
            if (
              updated.length > 0 &&
              updated[updated.length - 1].role === "assistant"
            ) {
              updated[updated.length - 1] = {
                ...updated[updated.length - 1],
                chartData,
              };
            } else {
              updated.push({ role: "assistant", content: "", chartData });
            }
            return updated;
          });
        } else if (data.type === "done") {
          setLoading(false);
          if (
            data.suggested_questions &&
            Array.isArray(data.suggested_questions)
          ) {
            setSuggestedQuestions(data.suggested_questions as string[]);
          }
          if (data.chat_log_id) {
            setChatLogId(data.chat_log_id as number);
            setFeedbackSent(false);
          }
          websocket.close();
        } else if (data.type === "error") {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content:
                "エラーが発生しました: " +
                ((data.message as string) || "不明なエラー"),
            },
          ]);
          setLoading(false);
          websocket.close();
        }
      };

      websocket.onerror = () => {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "接続エラーが発生しました。" },
        ]);
        setLoading(false);
      };

      websocket.onclose = () => {
        wsRef.current = null;
      };
    },
    [inputValue, loginUser]
  );

  const handleFeedback = async (feedback: "good" | "bad") => {
    if (!chatLogId || feedbackSent) return;
    setFeedbackSent(true);
    try {
      await postDataChatFeedback(chatLogId, feedback);
    } catch (e) {
      console.error("feedback error", e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.ctrlKey && e.key === "Enter") {
      e.preventDefault();
      if (!loading && inputValue.trim()) {
        handleSend();
      }
    }
  };

  const exampleQuestions = [
    "今月の契約数は？",
    "売上上位の商材は？",
    "パートナー別の開通数は？",
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* メッセージ領域 */}
      <Box sx={{ flex: 1, overflowY: "scroll", minHeight: 0, px: 1, py: 0.5 }}>
        {messages.length === 0 ? (
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <SmartToyIcon
                sx={{ fontSize: 28, color: "text.secondary", mb: 0.5 }}
              />
              <Paper
                elevation={0}
                sx={{
                  p: 1,
                  mt: 0,
                  bgcolor: "grey.100",
                  borderRadius: "4px 12px 12px 12px",
                  maxWidth: "85%",
                }}
              >
                <Typography sx={{ fontSize: "0.85rem", whiteSpace: "pre-wrap" }}>
                  {"データ分析AIです。DBデータについて質問してください。"}
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1 }}>
              {exampleQuestions.map((q) => (
                <Button
                  key={q}
                  variant="outlined"
                  fullWidth
                  sx={{
                    bgcolor: "white",
                    fontSize: "0.75rem",
                    justifyContent: "flex-start",
                  }}
                  onClick={() => handleSend(q)}
                >
                  {q}
                </Button>
              ))}
            </Box>
          </Box>
        ) : (
          messages.map((msg, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: msg.role === "assistant" ? "flex-start" : "flex-end",
                mb: 1,
              }}
            >
              {msg.role === "assistant" ? (
                <SmartToyIcon
                  sx={{ fontSize: 28, color: "text.secondary", mb: 0.5 }}
                />
              ) : (
                <PersonIcon
                  sx={{ fontSize: 28, color: "primary.main", mb: 0.5 }}
                />
              )}
              {msg.content && (
                <Paper
                  sx={{
                    display: "inline-block",
                    p: 0.75,
                    maxWidth: "90%",
                    bgcolor:
                      msg.role === "user" ? "primary.light" : "grey.100",
                    color:
                      msg.role === "user"
                        ? "primary.contrastText"
                        : "text.primary",
                  }}
                >
                  {msg.role === 'assistant' ? (
                    <Box sx={{
                      fontSize: '0.7rem',
                      '& p': { margin: '0.25em 0' },
                      '& h1, & h2, & h3, & h4': { margin: '0.5em 0 0.25em', fontSize: '0.8rem', fontWeight: 'bold' },
                      '& ul, & ol': { margin: '0.25em 0', paddingLeft: '1.2em' },
                      '& li': { margin: '0.1em 0' },
                      '& code': { backgroundColor: 'rgba(0,0,0,0.06)', padding: '0.1em 0.3em', borderRadius: '3px', fontSize: '0.65rem' },
                      '& pre': { backgroundColor: 'rgba(0,0,0,0.06)', padding: '0.5em', borderRadius: '4px', overflow: 'auto', margin: '0.25em 0' },
                      '& pre code': { backgroundColor: 'transparent', padding: 0 },
                      '& strong': { fontWeight: 'bold' },
                      '& a': { color: 'primary.main' },
                      '& table': { borderCollapse: 'collapse', margin: '0.25em 0', fontSize: '0.65rem' },
                      '& th, & td': { border: '1px solid #ddd', padding: '0.2em 0.4em' },
                    }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ whiteSpace: "pre-wrap", fontSize: "0.7rem" }}>
                      {msg.content}
                    </Typography>
                  )}
                </Paper>
              )}
              {msg.chartData && (
                <Box sx={{ width: "100%", mt: 0.5 }}>
                  <ChartRenderer data={msg.chartData} />
                </Box>
              )}
              {!loading &&
                chatLogId &&
                i === messages.length - 1 &&
                msg.role === "assistant" && (
                  <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleFeedback("good")}
                      disabled={feedbackSent}
                      color={feedbackSent ? "default" : "primary"}
                    >
                      <ThumbUpAltOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleFeedback("bad")}
                      disabled={feedbackSent}
                      color={feedbackSent ? "default" : "error"}
                    >
                      <ThumbDownAltOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
            </Box>
          ))
        )}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 0.5 }}>
            <CircularProgress size={16} />
          </Box>
        )}
        {suggestedQuestions.length > 0 && !loading && messages.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              px: 1,
              mt: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "0.65rem" }}
            >
              関連する質問:
            </Typography>
            {suggestedQuestions.map((q, idx) => (
              <Button
                key={idx}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  bgcolor: "white",
                  fontSize: "0.7rem",
                  justifyContent: "flex-start",
                  py: 0.25,
                }}
                onClick={() => {
                  setSuggestedQuestions([]);
                  handleSend(q);
                }}
              >
                {q}
              </Button>
            ))}
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* 入力欄 */}
      <Box sx={{ p: 1 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={4}
          maxRows={4}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="データについて質問..."
          disabled={loading}
          sx={{ "& .MuiInputBase-input": { fontSize: "0.75rem" } }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            mt: 0.5,
            gap: 0.5,
          }}
        >
          <Typography variant="caption" color="text.disabled">
            Ctrl+Enter で送信
          </Typography>
          <IconButton
            size="small"
            onClick={() => handleSend()}
            disabled={loading || !inputValue.trim()}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

export default DataChatPanel;
